import { AsyncPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import type { MatSidenav } from '@angular/material/sidenav';
import { Title } from '@angular/platform-browser';
import { Router, RouterOutlet } from '@angular/router';
import { AuthenticationService } from '@auth/authentication.service';
import { MenuController } from '@ionic/angular';
import { LoadingTrackerService } from '@services/tracker/loading-tracker.service';
import { LoadingModalComponent } from '@shared/modal/generic/loading-modal/loading-modal.component';
import { CommonUtils } from '@utils/common/common.utils';
import { DISPLAY_CONSTANTS } from './app.constants';
import { BannerComponent } from './components/banner/banner.component';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { TopNavComponent } from './components/top-nav/top-nav.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterOutlet,
    SideNavComponent,
    TopNavComponent,
    BannerComponent,
    AsyncPipe,
  ],
})
export class AppComponent implements OnInit {
  readonly commonUtils = inject(CommonUtils);
  private readonly authService = inject(AuthenticationService);
  private readonly destroy = inject(DestroyRef);
  private readonly loadingService = inject(LoadingTrackerService);
  private readonly matDialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly titleService = inject(Title);
  readonly menuController = inject(MenuController);

  readonly notifications = viewChild<MatSidenav>('notifications');

  private loadingRef: MatDialogRef<LoadingModalComponent> | null = null;

  constructor() {
    effect(() => {
      this.handleLoadingOverlay();
    });
  }

  ngOnInit(): void {
    this.titleService.setTitle(DISPLAY_CONSTANTS.APP_NAME.WEB);

    this.router.events.pipe(takeUntilDestroyed(this.destroy)).subscribe(() => {
      //On every route change close the banner and reset the file upload response
      this.commonUtils.fileUploadResponse.set({
        uniqueUploadId: '',
        fileName: '',
        fileSize: 0,
        accepted: 0,
        rejected: [],
        received: 0,
        itemsNonProcessed: [],
      });
      this.commonUtils.updateBanner(false);
    });

    this.matDialog.afterOpened
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(() => {
        //On every dialog open, close the banner and reset the file upload response
        this.commonUtils.fileUploadResponse.set({
          uniqueUploadId: '',
          fileName: '',
          fileSize: 0,
          accepted: 0,
          rejected: [],
          received: 0,
          itemsNonProcessed: [],
        });
        this.commonUtils.updateBanner(false);
      });

    this.authService.init();
  }

  private handleLoadingOverlay(): void {
    const shouldShow = this.loadingService.isLoading();

    if (shouldShow && !this.loadingRef) {
      this.loadingRef = this.matDialog.open(LoadingModalComponent, {
        disableClose: true,
        hasBackdrop: true,
        backdropClass: 'loading-overlay-backdrop',
        panelClass: 'loading-dialog-panel',
      });

      this.loadingRef.afterClosed().subscribe(() => {
        this.loadingRef = null;
      });
    }

    if (!shouldShow && this.loadingRef) {
      this.loadingRef.close();
    }
  }
}
